import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { authApi } from '@/api/auth';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { humanizeError } from '@/lib/errors';
import { notifyError } from '@/lib/haptics';
import { setLanguage } from '@/app/i18n';
import { LegalLink } from '@/components/LegalLink';
import { APP_BRAND, IS_APP_BUILD } from '@/lib/appBrand';
// `?inline` — картинка строкой data: внутри кода: в сборке сайта ветка IS_APP_BUILD выпадает вместе с ней, и в dist
// сайта не появляется ни файла, ни строки. Делает её assets-src/brand-2026-10/make_app_assets.py.
import appIcon from '@/assets/brand/app-icon-login.webp?inline';
import { privacyFallback, privacyUrl, termsFallback, termsUrl } from '@/lib/legalLinks';
import { nextFromSearch, studentLanding } from '@/lib/loginReturn';

type LoginFormData = {
  email: string;
  password: string;
};

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const currentUser = useAuthStore((s) => s.user);
  const isAuthed = useAuthStore((s) => Boolean(s.accessToken));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Сессия кончилась посреди урока — после входа ученик возвращается туда же (техдолг 299). Только /app/…;
  // сотрудник всегда в свой кабинет.
  const next = nextFromSearch(useLocation().search);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  // Already signed in → send them to their dashboard instead of the form.
  if (isAuthed && currentUser) {
    const landing =
      currentUser.role === 'admin' ? '/admin' :
      currentUser.role === 'curator' ? '/curator' :
      currentUser.role === 'student' ? (next ?? studentLanding()) : '/app';
    return <Navigate to={landing} replace />;
  }

  const onSubmit = async (data: LoginFormData) => {
    setSubmitting(true);
    setError(null);
    try {
      const resp = await authApi.login(data.email, data.password);
      setAuth(resp.user, resp.access_token, resp.refresh_token);
      const landing =
        resp.user.role === 'admin' ? '/admin' :
        resp.user.role === 'curator' ? '/curator' : studentLanding();
      navigate((resp.user.role === 'student' ? next : null) ?? landing, { replace: true });
    } catch (e) {
      notifyError();
      setError(humanizeError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title={t('auth.signIn')}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          type="email"
          autoComplete="email"
          label={t('auth.email')}
          // D76: пример адреса в store-сборке — без «onebilim» (на экране это бренд, а не домен); сайт — прежний.
          placeholder={IS_APP_BUILD ? 'you@example.com' : 'you@onebilim.kz'}
          {...register('email', { required: true })}
          error={errors.email && t('auth.errorEmail')}
        />
        <Input
          type="password"
          autoComplete="current-password"
          label={t('auth.password')}
          {...register('password', { required: true })}
          error={errors.password && t('auth.errorPassword')}
        />

        {error && <p className="text-sm text-error">{error}</p>}

        <Button type="submit" size="lg" fullWidth loading={submitting}>
          {t('auth.signIn')}
        </Button>

        {/* Пароля у большинства учеников нет вовсе: регистрация его не просит,
            вход идёт кодом из письма. Поэтому ссылка стоит сразу под кнопкой, а
            не прячется среди мелких. */}
        <button
          type="button"
          onClick={() => navigate('/login-code', { state: next ? { next } : undefined })}
          className="w-full text-center text-sm font-semibold text-primary"
        >
          {t('auth.signInByCode')}
        </button>

        <div className="flex justify-between pt-2 text-sm">
          <Link to="/forgot-password" className="text-on-surface-variant hover:text-primary">
            {t('auth.forgotPassword')}
          </Link>
          <Link to="/register" className="font-semibold text-primary">
            {t('auth.signUp')}
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}

// Reusable visual shell for /login, /register, /forgot, /reset.
// Kept here (private) — exported only if a 5th auth screen needs it.
export function AuthShell({
  title,
  children,
  back = true,
}: {
  title: string;
  children: React.ReactNode;
  /** Показывать ли выход назад. Экран подтверждения почты отключает: там свой «Позже». */
  back?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  /**
   * Выход с экрана входа.
   *
   * ЗАЧЕМ ОТДЕЛЬНАЯ КНОПКА. На айфоне нет системной кнопки «назад», а браузерной
   * панели в приложении из магазина тоже нет: человек, открывший вход из
   * любопытства, оказывался в тупике и мог только закрыть приложение. Логотип
   * сверху — ссылка на главную, но догадаться нажать на логотип невозможно.
   *
   * Если в истории есть куда вернуться — возвращаемся туда (например, на витрину
   * курсов, откуда пришли). Если экран открыт первым (запуск приложения, ссылка
   * из письма) — ведём на главную, иначе кнопка молча ничего не сделает.
   */
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/', { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Переключатель языка. До 22.08.2026 сменить язык можно было только в
            профиле — то есть уже ВОЙДЯ. Платформа открывается на казахском, и
            человек, который его не читает, упирался в форму входа, которую не
            понимает, без единого способа что-то поменять.
            ⚠️ В ПРИЛОЖЕНИИ ПЕРЕКЛЮЧАТЕЛЯ НЕТ (решение владельца 27.09.2026, сборка 61): экраны входа и
            регистрации — на русском (DEFAULT_LANG, app/i18n.ts), сменить язык — в профиле. Сайт — как было. */}
        {IS_APP_BUILD ? null : (
        <div className="mb-4 flex items-center justify-center gap-1">
          {(['kk', 'ru', 'en'] as const).map((lng) => (
            <button
              key={lng}
              type="button"
              onClick={() => setLanguage(lng)}
              aria-current={i18n.language.split('-')[0] === lng ? 'true' : undefined}
              className={
                i18n.language.split('-')[0] === lng
                  ? 'rounded-full bg-primary px-3 py-1.5 text-[13px] font-semibold text-white'
                  : 'rounded-full px-3 py-1.5 text-[13px] font-medium text-on-surface-variant hover:bg-surface-container-low'
              }
            >
              {lng === 'kk' ? 'Қаз' : lng === 'ru' ? 'Рус' : 'Eng'}
            </button>
          ))}
        </div>
        )}

        {back && (
          <button
            type="button"
            onClick={goBack}
            aria-label={t('common.back')}
            className="mb-2 -ml-3 grid h-11 w-11 place-items-center rounded-full text-primary transition hover:bg-[#FFF3E3] active:scale-95"
          >
            <span className="material-symbols-outlined text-[30px] leading-none">chevron_left</span>
          </button>
        )}
        {/* Ребрендинг 1.1.1 (решение владельца 05.10.2026): в приложении — иконка со скруглёнными углами (та же,
            что на домашнем экране и на заставке) и подпись «OneBilim Academy». Картинка — украшение, имя стоит
            подписью, поэтому alt пустой. На сайте — прежний «Onebilim». lib/appBrand.ts. */}
        <Link to="/" className="mb-8 block text-center">
          {IS_APP_BUILD ? (
            <img src={appIcon} alt="" width={80} height={80} className="mx-auto mb-3 h-20 w-20 rounded-[18px] shadow-sm" />
          ) : null}
          <span
            className={
              IS_APP_BUILD
                ? 'font-display text-[26px] font-extrabold tracking-display text-primary'
                : 'font-display text-3xl font-extrabold tracking-display text-primary'
            }
          >
            {IS_APP_BUILD ? APP_BRAND : 'Onebilim'}
          </span>
        </Link>
        <Card className="p-8">
          <h1 className="mb-6 font-display text-2xl font-bold tracking-display text-on-surface">
            {title}
          </h1>
          {children}
        </Card>

        {/* Политика и условия — прямо под формой. Их требуют и Apple, и Google:
            человек должен видеть, на что соглашается, ДО регистрации, а не
            искать по сайту. Адреса — из lib/legalLinks.ts (одно место на всё
            приложение); внешний адрес в приложении открывается системным окном. */}
        <p className="mt-6 text-center text-xs leading-relaxed text-on-surface-variant">
          {t('auth.legalIntro')}{' '}
          <LegalLink url={privacyUrl()} fallback={privacyFallback()} className="font-semibold text-primary underline-offset-2 hover:underline">
            {t('auth.legalPrivacy')}
          </LegalLink>{' '}
          {t('auth.legalAnd')}{' '}
          <LegalLink url={termsUrl()} fallback={termsFallback()} className="font-semibold text-primary underline-offset-2 hover:underline">
            {t('auth.legalTerms')}
          </LegalLink>
          .
        </p>
      </div>
    </div>
  );
}
